import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { api, type SearchResponse } from '@/lib/api';
import { fmtSeconds } from '@/lib/utils';

export function SearchBar({ courseSlug }: { courseSlug?: string }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [dismissed, setDismissed] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const searchQ = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => api.get<SearchResponse>(`/api/course/search?q=${encodeURIComponent(debounced)}&limit=20`),
    enabled: debounced.length >= 2,
    staleTime: 30_000,
  });

  const results = searchQ.data?.results ?? [];
  const showDropdown = !dismissed && debounced.length >= 2;

  // Close the dropdown on outside tap or Escape — it otherwise floats over the
  // page until the query is cleared or a result is picked (annoying on mobile).
  useEffect(() => {
    if (!showDropdown) return;
    const onDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setDismissed(true);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDismissed(true);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [showDropdown]);

  return (
    <div className="relative" ref={rootRef}>
      <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2 focus-within:border-primary/40">
        <MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
        <input
          type="search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setDismissed(false);
          }}
          placeholder="Suche im Kurs…"
          // 16px+ font-size required to stop iOS Safari from auto-zooming
          // when the input gains focus. Visually still feels "search-like".
          className="w-full bg-transparent text-base outline-none placeholder:text-muted-foreground sm:text-sm"
        />
        {q && (
          <button
            type="button"
            onClick={() => setQ('')}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Löschen
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="absolute left-0 right-0 top-full z-30 mt-2 max-h-96 overflow-y-auto rounded-lg border bg-popover text-popover-foreground shadow-xl">
          {searchQ.isLoading && (
            <div className="px-4 py-3 text-sm text-muted-foreground">Suche …</div>
          )}
          {searchQ.isError && (
            <div className="px-4 py-3 text-sm text-muted-foreground">
              Suche fehlgeschlagen. Bitte versuch es erneut.
            </div>
          )}
          {!searchQ.isLoading && !searchQ.isError && results.length === 0 && (
            <div className="px-4 py-3 text-sm text-muted-foreground">
              Keine Treffer für „{debounced}“.
            </div>
          )}
          {results.map((r, idx) => (
            <button
              key={`${r.lessonId}-${r.startSeconds}-${idx}`}
              type="button"
              onClick={() => {
                setQ('');
                navigate(
                  courseSlug
                    ? `/kurse/${courseSlug}/${r.moduleId}/${r.lessonId}?t=${Math.floor(r.startSeconds)}`
                    : `/kurse/masterclass/${r.moduleId}/${r.lessonId}?t=${Math.floor(r.startSeconds)}`,
                );
              }}
              className="block w-full border-b px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-muted/50"
            >
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="truncate font-medium text-foreground">
                  {r.lessonTitle}
                </span>
                <span className="shrink-0 tabular-nums">{fmtSeconds(r.startSeconds)}</span>
              </div>
              <div
                className="mt-1 text-sm text-foreground/90 [&_mark]:bg-primary/30 [&_mark]:px-0.5 [&_mark]:text-foreground"
                dangerouslySetInnerHTML={{ __html: r.snippet }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
